import { CalendarIcon, DownloadIcon, MoveHorizontal, PenLine, Send } from 'lucide-react'
import { Button } from '@klear/ui/components/button'
import { Card, CardContent, CardHeader, CardTitle } from '@klear/ui/components/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@klear/ui/components/select'
import BalanceChart from '@/app/klear-account/balance/balance-chart'
import { RecentTransactions } from '@/app/klear-account/recent-transactions'
import { Link, useParams, useSearchParams, useNavigate } from 'react-router'
import {
  DEFAULT_FINANCIAL_HISTORY_WINDOW_RANGE,
  PERMISSION_ACTION_EDIT,
  PERMISSION_ACTION_VIEW,
  PERMISSION_ENTITY_TREASURY,
  ROUTE_KLEAR_ACCOUNT,
  ROUTE_MAKE_PAYMENT,
  ROUTE_TRANSFER_FUNDS,
  ROUTE_SSE_STREAM
} from '@klear/constants'
import { SSE_EVENT_TYPE_KLEAR_ACCOUNT_UPDATE, type KlearAccountSSEMessage } from '@klear/sse'
import { useTrader } from '@klear/ui/hooks/use-trader'
import { useState, useEffect, useCallback } from 'react'
import BalanceHeader from '@/app/klear-account/balance/balance-header'
import {
  useKlearFinancialAccount,
  useKlearFinancialAccountMutations,
  klearAccountOptionLabel
} from '@klear/ui/hooks/use-klear-financial-account'
import { useEventSource } from '@klear/ui/hooks/use-event-source'
import { useUnsignedAchAccounts } from '@/app/legal-agreement/use-legal-agreement'
import KlearAccountLanding from '@/app/klear-account/klear-account-landing'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { useKlearAccountTransactions } from '@/hooks/use-klear-account-transactions'
import { ButtonLink } from '@klear/ui/components/button-link'
import Documents, { KlearFinancialAccountDetails } from '@/app/documents'
import FifthThirdLogo from '@/assets/fifth-third-logo.svg'
import LoadingSpinner from '@klear/ui/components/spinner'
import { UnverifiedBankAccountBanner } from '@/components/unverified-bank-account-banner'
import { GeneratedLegalAgreementViewer } from '@/app/legal-agreement/generated-legal-agreement-viewer'
import { trpc } from '@klear/ui/lib/trpc'
import { getApiUrl } from '@klear/runtime-env'
import toNumber from 'lodash/toNumber'
import toString from 'lodash/toString'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

export default function KlearAccount() {
  useDocumentTitle('Klear Account')
  const { user } = useAuth()
  const { traderId } = useParams()
  const { urlPrefixApp, trader } = useTrader(traderId)
  const { klearFinancialAccount, isLoading, accounts, hasMultipleAccounts, setSelectedAccountId } =
    useKlearFinancialAccount()
  const { refreshKlearFinancialAccount } = useKlearFinancialAccountMutations()
  const { achAgreement, unsignedKlearAccounts } = useUnsignedAchAccounts()
  const utils = trpc.useUtils()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const shouldRefresh = searchParams.get('shouldRefresh') === '1'
  const [showSigningModal, setShowSigningModal] = useState(false)

  const needsSignedAgreement =
    klearFinancialAccount?.id != null &&
    unsignedKlearAccounts.some((a: { id: number }) => a.id === klearFinancialAccount.id)

  const [transactionWindow, setTransactionWindow] = useState(DEFAULT_FINANCIAL_HISTORY_WINDOW_RANGE.toString())
  const financialHistoryDayRange = parseInt(transactionWindow)
  const { transactions, downloadTransactions } = useKlearAccountTransactions(financialHistoryDayRange)

  const handleSSEMessage = useCallback(
    async (data: KlearAccountSSEMessage) => {
      if (data.type === SSE_EVENT_TYPE_KLEAR_ACCOUNT_UPDATE && klearFinancialAccount) {
        handleRefreshKlearBankAccount()
      }
    },
    [klearFinancialAccount]
  )

  const handleSSEError = useCallback((error: Event) => {
    console.error('Klear account SSE connection error:', error)
  }, [])

  const sseUrl = trader?.organizationId
    ? `${getApiUrl()}/${ROUTE_KLEAR_ACCOUNT}/${ROUTE_SSE_STREAM}?traderId=${trader.id}`
    : ''

  useEventSource<KlearAccountSSEMessage>(sseUrl, {
    messageType: SSE_EVENT_TYPE_KLEAR_ACCOUNT_UPDATE,
    onMessage: handleSSEMessage,
    onError: handleSSEError
  })

  async function handleRefreshKlearBankAccount() {
    try {
      searchParams.delete('shouldRefresh')
      navigate({ search: searchParams.toString() }, { replace: true })
      await refreshKlearFinancialAccount(klearFinancialAccount?.id)
    } catch (error) {
      console.error('Error refreshing Klear bank account:', error)
    }
  }

  useEffect(() => {
    const showPaymentModal = searchParams.get('showPaymentModal') === 'true'
    if (showPaymentModal) {
      searchParams.delete('showPaymentModal')
      navigate({ search: searchParams.toString() }, { replace: true })
    }
  }, [searchParams, navigate])

  useEffect(() => {
    if (shouldRefresh && klearFinancialAccount) {
      handleRefreshKlearBankAccount()
    }
  }, [shouldRefresh, klearFinancialAccount])

  if (!user.hasPermission(PERMISSION_ENTITY_TREASURY, PERMISSION_ACTION_VIEW)) {
    return null
  }

  const transactionDaysOptions = [
    { value: '30', label: '30 days' },
    { value: '60', label: '60 days' },
    { value: '90', label: '90 days' },
    { value: '120', label: '120 days' },
    { value: '0', label: 'All time' }
  ] as const

  if (isLoading) {
    return <LoadingSpinner />
  }

  return (
    <>
      <div className="flex flex-col p-2 gap-6">
        <UnverifiedBankAccountBanner className="mb-6" />
        {!klearFinancialAccount ? (
          <KlearAccountLanding />
        ) : (
          <>
            {user.hasPermission(PERMISSION_ENTITY_TREASURY, PERMISSION_ACTION_EDIT) && (
              <div className="flex flex-wrap gap-4">
                <ButtonLink Link={Link} variant="outline" to={`${urlPrefixApp}/${ROUTE_MAKE_PAYMENT}`}>
                  <Send className="mr-2 h-4 w-4" />
                  Make a Payment
                </ButtonLink>
                <ButtonLink Link={Link} variant="outline" to={`${urlPrefixApp}/${ROUTE_TRANSFER_FUNDS}`}>
                  <MoveHorizontal className="mr-2 h-4 w-4" />
                  Transfer Funds
                </ButtonLink>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-3">
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <CardTitle className="text-base font-medium">Klear Account Balance</CardTitle>
                      <div className="flex items-center gap-2">
                        {hasMultipleAccounts && klearFinancialAccount?.id != null && (
                          <Select
                            value={toString(klearFinancialAccount.id)}
                            onValueChange={(value) => {
                              setShowSigningModal(false)
                              setSelectedAccountId(toNumber(value))
                            }}
                          >
                            <SelectTrigger className="h-8 w-56 text-xs" aria-label="Klear account">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {accounts.map((account) => (
                                <SelectItem key={account.id} value={toString(account.id)}>
                                  {klearAccountOptionLabel(account)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        <Select value={transactionWindow} onValueChange={setTransactionWindow}>
                          <SelectTrigger className="h-8 w-32 text-xs">
                            <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {transactionDaysOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardHeader>
                  <div className="relative">
                    <CardContent>
                      <div className="flex flex-col">
                        <BalanceHeader historyWindowDays={financialHistoryDayRange} />
                      </div>
                      <div className="mt-4">
                        <BalanceChart historyWindowDays={financialHistoryDayRange} />
                      </div>
                      <div className="-mx-6 border-t"></div>
                      <div className="py-3">
                        <div className="text-base font-medium">Klear Financial Account Details</div>
                      </div>
                      <div className="p-0">
                        <div className="">
                          <div className="text-sm font-medium text-muted-foreground">Bank Name</div>

                          <div className="text-sm font-semibold flex gap-1">
                            <img src={FifthThirdLogo} alt="Fifth Third Bank Logo" width={20} height={20} />{' '}
                            <p>{klearFinancialAccount?.bankName}*</p>
                          </div>
                          <p className="text-xs text-foreground mt-3 ml-6 text-right">
                            Klear partners with Stripe Payments Company for money transmission services and account
                            services with funds held at Fifth Third Bank N.A., Member FDIC
                          </p>
                        </div>
                      </div>
                    </CardContent>
                    {needsSignedAgreement && (
                      <div
                        className="absolute inset-0 rounded-b-lg bg-background/80 backdrop-blur-[2px] flex flex-col items-center justify-center gap-4 z-10 p-6"
                        aria-label="Sign agreement required"
                      >
                        <p className="text-center font-medium text-foreground">
                          Your Klear account agreement must be signed before you can use this account.
                        </p>
                        <Button size="lg" onClick={() => setShowSigningModal(true)} className="gap-2">
                          <PenLine className="h-4 w-4" />
                          Sign agreement
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              </div>
            </div>

            <KlearFinancialAccountDetails />
            <Documents />

            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">Recent Transactions</h2>
              <div className="flex gap-2">
                {transactions.length > 0 && (
                  <Button variant="outline" size="sm" onClick={downloadTransactions}>
                    <DownloadIcon className="h-4 w-4" />
                    Download {financialHistoryDayRange ? ` last ${financialHistoryDayRange} days` : 'all transactions'}
                  </Button>
                )}
              </div>
            </div>

            <RecentTransactions historyWindowDays={financialHistoryDayRange} />

            {trader && klearFinancialAccount && achAgreement && (
              <GeneratedLegalAgreementViewer
                agreement={achAgreement}
                traderId={trader.id}
                open={showSigningModal}
                onClose={() => setShowSigningModal(false)}
                onSuccess={async () => {
                  setShowSigningModal(false)
                  await refreshKlearFinancialAccount(klearFinancialAccount.id)
                  await utils.klearAccount.getSignedLegalAgreementUrl.invalidate()
                }}
                klearAccountIds={[klearFinancialAccount.id!]}
              />
            )}
          </>
        )}
      </div>
    </>
  )
}
